import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { FilterType } from '@/models/FilterType';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import Tag from '../dataDisplay/Tag';
import useFilterParams from '@/utils/UseFilterParams';
import ConditionalRenderer from '../ConditionalRenderer';
import IconButton from '../buttons/IconButton';
import useTranslateFilter from '@/utils/translateFilter';
import { unitsQueryKeys } from '@/data/services/querykeys';

export type QuickFilterProps = {
  filters?: string[];
  type: QuickFilterType;
};

type QuickFilterType = FilterType;

export default function QuickFilter({ filters = [], type }: QuickFilterProps) {
  const { translateItem } = useTranslateFilter();

  const unitsQueries = useQueries({
    queries: filters?.map(filter => ({
      ...unitsQueryKeys.get(Number(filter)),
      keepPreviousData: true,
      enabled: !!filter && (type === 'unitId' || type === 'unit'),
      staleTime: REQUEST_STALE_TIME_IN_MS,
      placeholderData: {
        id: filter,
        name: '',
      },
    })),
  });

  const units = unitsQueries.map(
    ({ data, isPlaceholderData: isLoading, status }) => {
      return {
        unit: data,
        loading: isLoading,
        status,
      };
    },
  );

  type QuickFilterItem = {
    value?: string;
    id?: string;
    loading?: boolean;
    status?: string;
  };

  const list = useMemo(() => {
    let unitItems: QuickFilterItem[] = [];
    let statusItems: QuickFilterItem[] = [];
    let userStatusItems: QuickFilterItem[] = [];
    let userTypeItems: QuickFilterItem[] = [];
    let enrollmentItems: QuickFilterItem[] = [];

    switch (type) {
      case 'unitId':
      case 'unit': {
        unitItems = units.map(({ unit, loading, status }) => {
          return {
            value: unit?.name,
            id: String(unit?.id),
            loading,
            status,
          };
        });
        return unitItems;
      }
      case 'klassStatus': {
        statusItems = filters.map(item => {
          return {
            value: translateItem({ type, item }),
            id: item,
          };
        });
        return statusItems;
      }
      case 'userStatus':
      case 'staffStatus': {
        userStatusItems = filters.map(item => {
          return {
            value: translateItem({ type, item }),
            id: item,
          };
        });
        return userStatusItems;
      }
      case 'userType': {
        userTypeItems = filters.map(item => {
          return {
            value: translateItem({ type, item }),
            id: item,
          };
        });
        return userTypeItems;
      }
      case 'enrollmentStatus': {
        enrollmentItems = filters.map(item => {
          return {
            value: translateItem({ type, item }),
            id: item,
          };
        });
        return enrollmentItems;
      }
      default:
        return [];
    }
  }, [filters, translateItem, type, units]);

  const { deleteFilterValue, deleteFilterParams } = useFilterParams({});

  const filterType = ['klassStatus', 'userStatus', 'staffStatus'].includes(type)
    ? 'status'
    : type;

  return (
    <ConditionalRenderer condition={list.length}>
      <li className="flex items-center w-fit">
        <ul className="flex flex-wrap items-center gap-1 justify-end">
          {list.map(item => (
            <Tag
              loading={item?.loading}
              key={item?.id}
              className={{
                base: 'rounded-full px-3 py-1 text-14 font-500 gap-2 flex-row-reverse truncate',
              }}
              text={item.value ?? ''}
              icon={<XMarkIcon className="w-4 cursor-pointer" />}
              onClick={() => {
                deleteFilterValue(filterType, item.id, { pageNumber: 1 });
              }}
            />
          ))}
        </ul>
        <ConditionalRenderer condition={list.length > 1}>
          <IconButton
            className="px-2"
            icon={<XMarkIcon className="w-4 cursor-pointer text-error" />}
            onClick={() => {
              deleteFilterParams(filterType, { pageNumber: 1 });
            }}
          />
        </ConditionalRenderer>
      </li>
    </ConditionalRenderer>
  );
}
